import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { Layer, Marker, Source } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import Map from "../MapWhenReady/MapWhenReady";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, TYPE } from "../../utils/designTokens";
import { labelSx, metaSx } from "./checkoutUi";

const MAP_HEIGHT = 180;

/**
 * Where a pickup order is collected. A cart built at a food-truck stop is
 * collected at that stop, during its hours.
 */
export function pickupPoint(restaurant) {
  const stop = restaurant?.orderingStop?.stop || null;
  const address = stop?.address || restaurant?.serviceLocation?.address || restaurant?.address || "";
  const truckLabel = stop
    ? [restaurant?.orderingStop?.truckName, stop.label].filter(Boolean).join(" · ") || null
    : restaurant?.isMobile ? restaurant?.serviceLocation?.label || null : null;
  const hours = stop
    ? stop.windowLabel
    : restaurant?.isMobile && restaurant?.serviceLocation?.isOpenNow
      ? restaurant?.serviceLocation?.windowLabel
      : null;
  return {
    name: truckLabel ? `${restaurant?.name} · ${truckLabel}` : restaurant?.name || "",
    address,
    until: hours ? hours.split("–").pop().trim() : null,
  };
}

function YouPin() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box sx={{ px: 1, py: 0.25, borderRadius: RADIUS.pill, bgcolor: C.ink, color: "#FFFFFF", fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, lineHeight: "18px", boxShadow: "0 2px 6px rgba(26,25,24,0.3)" }}>
        You
      </Box>
      <Box sx={{ width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: `6px solid ${C.ink}` }} />
    </Box>
  );
}

function RestaurantPin() {
  return (
    <Box sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: C.bg, border: `2px solid ${C.red}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(26,25,24,0.25)" }}>
      <StorefrontRoundedIcon sx={{ fontSize: 18, color: C.red }} />
    </Box>
  );
}

/**
 * The address block of step 1: a map from the customer to the restaurant with
 * a dashed red line between them, then where the order goes (delivery, with
 * Change) or where it is collected (pickup).
 */
function CheckoutAddressCard({ isPickUp, location, restaurant, restaurantCoords, onChangeAddress }) {
  const mapRef = useRef(null);
  const you = location?.latitude != null && location?.longitude != null
    ? { lat: parseFloat(location.latitude), lng: parseFloat(location.longitude) }
    : null;
  const rest = restaurantCoords && Number.isFinite(restaurantCoords.lat) && Number.isFinite(restaurantCoords.lng)
    ? restaurantCoords
    : null;

  const route = useMemo(() => (you && rest
    ? { type: "Feature", geometry: { type: "LineString", coordinates: [[you.lng, you.lat], [rest.lng, rest.lat]] } }
    : null), [you?.lat, you?.lng, rest?.lat, rest?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const pts = [you, rest].filter(Boolean);
    if (!pts.length) return;
    if (pts.length === 1) {
      map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 14, duration: 0 });
      return;
    }
    const lngs = pts.map((p) => p.lng);
    const lats = pts.map((p) => p.lat);
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: { top: 44, bottom: 28, left: 48, right: 48 }, duration: 0, maxZoom: 15 }
    );
  }, [you?.lat, you?.lng, rest?.lat, rest?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fit();
  }, [fit]);

  const pickup = isPickUp ? pickupPoint(restaurant) : null;
  const unitParts = [
    location?.label,
    location?.apartment,
    location?.buildingName,
    location?.entryCode && `Code ${location.entryCode}`,
  ].filter(Boolean);

  return (
    <Box sx={{ borderRadius: RADIUS.menuCard, border: `1px solid ${C.border}`, overflow: "hidden", bgcolor: C.bg }}>
      <Box
        role="img"
        aria-label={isPickUp ? "Map of the restaurant" : "Map from the restaurant to your address"}
        sx={{ height: MAP_HEIGHT, bgcolor: C.surface2, "& .mapboxgl-ctrl-logo": { transform: "scale(0.8)" } }}
      >
        <Map
          ref={mapRef}
          initialViewState={{ longitude: rest?.lng ?? you?.lng ?? 0, latitude: rest?.lat ?? you?.lat ?? 0, zoom: 13 }}
          style={{ height: MAP_HEIGHT, width: "100%" }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          interactive={false}
          onLoad={fit}
        >
          {route && (
            <Source id="checkout-route" type="geojson" data={route}>
              <Layer
                id="checkout-route-line"
                type="line"
                layout={{ "line-cap": "round", "line-join": "round" }}
                paint={{ "line-color": C.red, "line-width": 3, "line-dasharray": [1.2, 1.6] }}
              />
            </Source>
          )}
          {you && (
            <Marker longitude={you.lng} latitude={you.lat} anchor="bottom">
              <YouPin />
            </Marker>
          )}
          {rest && (
            <Marker longitude={rest.lng} latitude={rest.lat} anchor="center">
              <RestaurantPin />
            </Marker>
          )}
        </Map>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, borderTop: `1px solid ${C.border}` }}>
        <PlaceOutlinedIcon aria-hidden="true" sx={{ fontSize: 22, color: C.red, mt: "1px", flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ ...labelSx, color: C.muted }}>{isPickUp ? "Pick up from" : "Deliver to"}</Box>
          {isPickUp ? (
            <>
              <Box sx={{ ...TYPE.subHeading, color: C.ink, mt: 0.25, wordBreak: "break-word" }}>{pickup.name}</Box>
              {pickup.address && <Box sx={{ ...metaSx, mt: 0.25, wordBreak: "break-word" }}>{pickup.address}</Box>}
              {pickup.until && <Box sx={{ ...metaSx, color: C.green, fontWeight: 600, mt: 0.25 }}>{`Here until ${pickup.until}`}</Box>}
            </>
          ) : (
            <>
              <Box sx={{ ...TYPE.subHeading, color: location?.deliveryAddress ? C.ink : C.muted, mt: 0.25, wordBreak: "break-word" }}>
                {location?.deliveryAddress || "Add a delivery address"}
              </Box>
              {unitParts.length > 0 && <Box sx={{ ...metaSx, mt: 0.25, wordBreak: "break-word" }}>{unitParts.join(" · ")}</Box>}
            </>
          )}
        </Box>
        {!isPickUp && (
          <Box
            component="button"
            type="button"
            onClick={onChangeAddress}
            aria-label={location?.deliveryAddress ? "Change delivery address" : "Add a delivery address"}
            sx={{
              ...BUTTON_RESET,
              flexShrink: 0,
              height: 44,
              px: 2,
              borderRadius: RADIUS.control,
              border: `1px solid ${C.borderStrong}`,
              fontFamily: FONT_BODY,
              fontSize: 14,
              fontWeight: 600,
              color: C.ink,
              "&:hover": { borderColor: C.ink },
              ...FOCUS_RING,
            }}
          >
            {location?.deliveryAddress ? "Change" : "Add"}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default React.memo(CheckoutAddressCard);
